import React, { useContext, useEffect, useState } from "react";
import {
  EditPopover,
  NumberInput,
  Panel,
  RadioButtonInput,
  RangeNumberInput,
  SelectInput,
  Tabs,
  TabsV2,
  TextInput,
  ToggleInput,
  UnitInput,
} from "../input";
import { GrigoraEditorContext } from "../editor/context";
import { isEmpty } from "lodash";
import GrigoraCSSRenderer from "../blocks/css-render";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGear,
  faPlay,
  faRefresh,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { GrigoraDashboardContext } from "../dashboard/context";
import { isUpdatedFromDefault } from "../utils";
import { INPUT_LIMITS } from "../input-limits";

function EntranceAnimations({ block, actions, ...props }) {
  const {
    id,
    props: { entranceAnimation = {} },
    dom,
  } = block;

  const { setProp } = actions;

  const editorContext = useContext(GrigoraEditorContext);
  const dashboardContext = useContext(GrigoraDashboardContext);

  const [active, setActive] = useState("desktop");
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);

  var gsap = editorContext?.gsap;
  var editorDocument = dashboardContext?.editorDocument;

  useEffect(() => {
    if (editorContext?.displaySize) {
      setActive(editorContext.displaySize);
    }
  }, [editorContext?.displaySize]);

  function changeConfig(transform, val) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        animationConfig: {
          ...(entranceAnimation?.animationConfig
            ? entranceAnimation?.animationConfig
            : {}),
          [transform]: val,
        },
      };
    }, 300);
  }

  function changeEntranceProp(prop, val) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        [prop]: val,
      };
    }, 300);
  }

  function changeEntranceDeepProp(prop, deep, val) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        [prop]: {
          ...(entranceAnimation?.[prop] ? entranceAnimation?.[prop] : {}),
          [deep]: val,
        },
      };
    }, 300);
  }

  function changeConfigDeep(transform, axis, val) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        animationConfig: {
          ...(entranceAnimation?.animationConfig
            ? entranceAnimation?.animationConfig
            : {}),
          [transform]: {
            ...(entranceAnimation?.animationConfig?.[transform]
              ? entranceAnimation?.animationConfig?.[transform]
              : {}),
            [axis]: val,
          },
        },
      };
    }, 300);
  }

  function removeConfigDeep(transform, axis) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        animationConfig: {
          ...(entranceAnimation?.animationConfig
            ? entranceAnimation?.animationConfig
            : {}),
          [transform]: {
            ...(entranceAnimation?.animationConfig?.[transform]
              ? entranceAnimation?.animationConfig?.[transform]
              : {}),
            [axis]: undefined,
          },
        },
      };
    }, 300);
  }

  function removeConfig(transform) {
    setProp((props) => {
      props.entranceAnimation = {
        ...(entranceAnimation ? entranceAnimation : {}),
        animationConfig: {
          ...(entranceAnimation?.animationConfig
            ? entranceAnimation?.animationConfig
            : {}),
          [transform]: undefined,
        },
      };
    }, 300);
  }

  return (
    <>
      <Panel
        isUpdated={isUpdatedFromDefault([
          { key: entranceAnimation, default: {} },
        ])}
        id={`block-setting-${id}-entrance-animations`}
        title={"Entrance Animations"}
        initialOpen={false}
      >
        <>
          {!(entranceAnimation && isEmpty(entranceAnimation)) && (
            <>
              <div className="flex text-xs justify-between mb-2">
                Reset Animations
                <div
                  onClick={() => {
                    setProp((props) => {
                      props.entranceAnimation = undefined;
                    });
                  }}
                  className="mr-2 cursor-pointer"
                >
                  <FontAwesomeIcon icon={faRefresh} size="sm" />
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end">
            <div className="flex gap-[5px]">
              <div
                onClick={(e) => {
                  if (gsap && editorDocument) {
                    var elements = editorDocument.querySelectorAll(
                      `.entrance-${id}`,
                    );
                    var config = {
                      x: entranceAnimation?.animationConfig?.shift?.x,
                      y: entranceAnimation?.animationConfig?.shift?.y,
                      z: entranceAnimation?.animationConfig?.shift?.z,
                      rotationX: entranceAnimation?.animationConfig?.rotate?.x,
                      rotationY: entranceAnimation?.animationConfig?.rotate?.y,
                      rotationZ: entranceAnimation?.animationConfig?.rotate?.z,
                      skewX: entranceAnimation?.animationConfig?.skew?.x,
                      skewY: entranceAnimation?.animationConfig?.skew?.y,
                      scaleX: entranceAnimation?.animationConfig?.scale?.x,
                      scaleY: entranceAnimation?.animationConfig?.scale?.y,
                      opacity: entranceAnimation?.animationConfig?.opacity,
                      perspective:
                        entranceAnimation?.animationConfig?.perspective,
                      duration: entranceAnimation?.animationConfig?.duration
                        ? Number(entranceAnimation?.animationConfig?.duration) /
                          1000
                        : 1,
                      delay: entranceAnimation?.animationConfig?.delay
                        ? Number(entranceAnimation?.animationConfig?.delay) /
                          1000
                        : 0,
                      repeat: entranceAnimation?.animationConfig?.infinite
                        ? -1
                        : undefined,
                      yoyo: entranceAnimation?.animationConfig?.yoyo
                        ? entranceAnimation?.animationConfig?.yoyo
                        : undefined,
                      ease: entranceAnimation?.easing,
                      transformOrigin: entranceAnimation?.transformOrigin
                        ? entranceAnimation?.transformOrigin
                        : undefined,
                      triggerAnchor: entranceAnimation?.triggerAnchor
                        ? entranceAnimation?.triggerAnchor
                        : undefined,
                      autoAlpha: entranceAnimation?.initiallyHidden
                        ? 0
                        : undefined,
                      onComplete: function () {
                        gsap.set(this.targets(), { clearProps: "all" });
                      },
                      onStart: function () {
                        this.targets().map((el) => {
                          el.style.visibility = "visible";
                        });
                      },
                    };
                    gsap.killTweensOf(elements);
                    if (entranceAnimation?.direction === "to") {
                      gsap.to(elements, config);
                    } else {
                      gsap.from(elements, config);
                    }
                  }
                }}
                title="Play Animation"
                className="cursor-pointer"
              >
                <FontAwesomeIcon icon={faPlay} size="xs" />
              </div>
              <div
                onClick={(e) => {
                  if (gsap && editorDocument) {
                    var elements = editorDocument.querySelectorAll(
                      `.entrance-${id}`,
                    );
                    gsap.killTweensOf(elements);
                  }
                }}
                title="Stop Animation"
                className="cursor-pointer"
              >
                <FontAwesomeIcon icon={faStop} size="xs" />
              </div>
              <div></div>
            </div>
          </div>
          <SelectInput
            isResetVisible={false}
            value={entranceAnimation?.animationPreset}
            onChange={(val) => {
              if (val) {
                var config = undefined;

                if (val === "fade") {
                  config = {
                    opacity: 0,
                  };
                } else if (val === "fade-up") {
                  config = {
                    shift: {
                      y: 50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-down") {
                  config = {
                    shift: {
                      y: -50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-left") {
                  config = {
                    shift: {
                      x: -50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-right") {
                  config = {
                    shift: {
                      x: 50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-up-left") {
                  config = {
                    shift: {
                      x: -50,
                      y: 50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-up-right") {
                  config = {
                    shift: {
                      x: 50,
                      y: 50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-down-left") {
                  config = {
                    shift: {
                      x: -50,
                      y: -50,
                    },
                    opacity: 0,
                  };
                } else if (val === "fade-down-right") {
                  config = {
                    shift: {
                      x: 50,
                      y: -50,
                    },
                    opacity: 0,
                  };
                } else if (val === "flip-up") {
                  config = {
                    shift: {
                      y: 30,
                    },
                    rotate: {
                      x: -60,
                    },
                    perspective: 2500,
                    opacity: 0,
                  };
                } else if (val === "flip-down") {
                  config = {
                    shift: {
                      y: -30,
                    },
                    rotate: {
                      x: 60,
                    },
                    perspective: 2500,
                    opacity: 0,
                  };
                } else if (val === "flip-left") {
                  config = {
                    rotate: {
                      y: -100,
                    },
                    perspective: 2500,
                    opacity: 0,
                  };
                } else if (val === "flip-right") {
                  config = {
                    rotate: {
                      y: 100,
                    },
                    perspective: 2500,
                    opacity: 0,
                  };
                } else if (val === "slide-up") {
                  config = {
                    shift: {
                      y: "100%",
                    },
                    opacity: 1,
                  };
                } else if (val === "slide-down") {
                  config = {
                    shift: {
                      y: "-100%",
                    },
                    opacity: 1,
                  };
                } else if (val === "slide-left") {
                  config = {
                    shift: {
                      x: "-100%",
                    },
                    opacity: 1,
                  };
                } else if (val === "slide-right") {
                  config = {
                    shift: {
                      x: "100%",
                    },
                    opacity: 1,
                  };
                } else if (val === "zoom-in") {
                  config = {
                    scale: {
                      x: 0.6,
                      y: 0.6,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-in-up") {
                  config = {
                    shift: {
                      y: 30,
                    },
                    scale: {
                      x: 0.6,
                      y: 0.6,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-in-down") {
                  config = {
                    shift: {
                      y: -30,
                    },
                    scale: {
                      x: 0.6,
                      y: 0.6,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-in-left") {
                  config = {
                    shift: {
                      x: -60,
                    },
                    scale: {
                      x: 0.6,
                      y: 0.6,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-in-right") {
                  config = {
                    shift: {
                      x: 60,
                    },
                    scale: {
                      x: 0.6,
                      y: 0.6,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-out") {
                  config = {
                    scale: {
                      x: 1.25,
                      y: 1.25,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-out-up") {
                  config = {
                    shift: {
                      y: 30,
                    },
                    scale: {
                      x: 1.25,
                      y: 1.25,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-out-down") {
                  config = {
                    shift: {
                      y: -30,
                    },
                    scale: {
                      x: 1.25,
                      y: 1.25,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-out-left") {
                  config = {
                    shift: {
                      x: -60,
                    },
                    scale: {
                      x: 1.25,
                      y: 1.25,
                    },
                    opacity: 0,
                  };
                } else if (val === "zoom-out-right") {
                  config = {
                    shift: {
                      x: 60,
                    },
                    scale: {
                      x: 1.25,
                      y: 1.25,
                    },
                    opacity: 0,
                  };
                }

                setProp((props) => {
                  props.entranceAnimation = {
                    ...(entranceAnimation ? entranceAnimation : {}),
                    animationPreset: val,
                    animationConfig: config,
                  };
                });
              } else {
                setProp((props) => {
                  props.entranceAnimation = {
                    ...(entranceAnimation ? entranceAnimation : {}),
                    animationPreset: val,
                    animationConfig: undefined,
                  };
                });
              }
            }}
            options={[
              { label: "-", value: "" },
              { label: "Fade", value: "fade" },
              { label: "Fade Up", value: "fade-up" },
              { label: "Fade Down", value: "fade-down" },
              { label: "Fade Left", value: "fade-left" },
              { label: "Fade Right", value: "fade-right" },
              { label: "Fade Up Left", value: "fade-up-left" },
              { label: "Fade Up Right", value: "fade-up-right" },
              { label: "Fade Down Left", value: "fade-down-left" },
              { label: "Fade Down Right", value: "fade-down-right" },
              { label: "Flip Up", value: "flip-up" },
              { label: "Flip Down", value: "flip-down" },
              { label: "Flip Left", value: "flip-left" },
              { label: "Flip Right", value: "flip-right" },
              { label: "Slide Up", value: "slide-up" },
              { label: "Slide Down", value: "slide-down" },
              { label: "Slide Left", value: "slide-left" },
              { label: "Slide Right", value: "slide-right" },
              { label: "Zoom In", value: "zoom-in" },
              { label: "Zoom In Up", value: "zoom-in-up" },
              { label: "Zoom In Down", value: "zoom-in-down" },
              { label: "Zoom In Left", value: "zoom-in-left" },
              { label: "Zoom In Right", value: "zoom-in-right" },
              { label: "Zoom Out", value: "zoom-out" },
              { label: "Zoom Out Up", value: "zoom-out-up" },
              { label: "Zoom Out Down", value: "zoom-out-down" },
              { label: "Zoom Out Left", value: "zoom-out-left" },
              { label: "Zoom Out Right", value: "zoom-out-right" },
            ]}
            label="Animation"
          />
          <div className="text-xs">
            <div className="flex justify-between">
              <div>Config</div>
              <div
                onClick={(e) => {
                  setIsConfigExpanded(!isConfigExpanded);
                }}
                className="cursor-pointer select-none"
              >
                <FontAwesomeIcon icon={faGear} />
              </div>
            </div>
            {isConfigExpanded && (
              <>
                <div>
                  <EditPopover
                    label={"Shift"}
                    canReset={
                      !isEmpty(entranceAnimation?.animationConfig?.shift)
                    }
                    onReset={() => {
                      removeConfig("shift");
                    }}
                  >
                    <UnitInput
                      isNegativeValid
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.shift?.x ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("shift", "x", val);
                      }}
                      label={"X"}
                    />
                    <UnitInput
                      isNegativeValid
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.shift?.y ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("shift", "y", val);
                      }}
                      label={"Y"}
                    />
                    <UnitInput
                      isNegativeValid
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.shift?.z ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("shift", "z", val);
                      }}
                      label={"Z"}
                    />
                  </EditPopover>
                  <EditPopover
                    label={"Rotate"}
                    canReset={
                      !isEmpty(entranceAnimation?.animationConfig?.rotate)
                    }
                    onReset={() => {
                      removeConfig("rotate");
                    }}
                  >
                    <NumberInput
                      resetValue={""}
                      value={
                        entranceAnimation?.animationConfig?.rotate?.x ?? ""
                      }
                      onChange={(val) => {
                        changeConfigDeep("rotate", "x", val);
                      }}
                      label={"X"}
                      min={INPUT_LIMITS["entrance-animations-rotate-xyz"].min}
                      max={INPUT_LIMITS["entrance-animations-rotate-xyz"].max}
                      step={INPUT_LIMITS["entrance-animations-rotate-xyz"].step}
                    />
                    <NumberInput
                      resetValue={""}
                      value={
                        entranceAnimation?.animationConfig?.rotate?.y ?? ""
                      }
                      onChange={(val) => {
                        changeConfigDeep("rotate", "y", val);
                      }}
                      label={"Y"}
                      min={INPUT_LIMITS["entrance-animations-rotate-xyz"].min}
                      max={INPUT_LIMITS["entrance-animations-rotate-xyz"].max}
                      step={INPUT_LIMITS["entrance-animations-rotate-xyz"].step}
                    />
                    <NumberInput
                      resetValue={""}
                      value={
                        entranceAnimation?.animationConfig?.rotate?.z ?? ""
                      }
                      onChange={(val) => {
                        changeConfigDeep("rotate", "z", val);
                      }}
                      label={"Z"}
                      // min={-360}
                      // max={360}
                      // step={1}
                      min={INPUT_LIMITS["entrance-animations-rotate-xyz"].min}
                      max={INPUT_LIMITS["entrance-animations-rotate-xyz"].max}
                      step={INPUT_LIMITS["entrance-animations-rotate-xyz"].step}
                    />
                  </EditPopover>
                  <EditPopover
                    label={"Skew"}
                    canReset={
                      !isEmpty(entranceAnimation?.animationConfig?.skew)
                    }
                    onReset={() => {
                      removeConfig("skew");
                    }}
                  >
                    <NumberInput
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.skew?.x ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("skew", "x", val);
                      }}
                      label={"X"}
                      // min={-360}
                      // max={360}
                      // step={1}
                      min={INPUT_LIMITS["entrance-animations-skew-xy"].min}
                      max={INPUT_LIMITS["entrance-animations-skew-xy"].max}
                      step={INPUT_LIMITS["entrance-animations-skew-xy"].step}
                    />
                    <NumberInput
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.skew?.y ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("skew", "y", val);
                      }}
                      label={"Y"}
                      // min={-360}
                      // max={360}
                      // step={1}
                      min={INPUT_LIMITS["entrance-animations-skew-xy"].min}
                      max={INPUT_LIMITS["entrance-animations-skew-xy"].max}
                      step={INPUT_LIMITS["entrance-animations-skew-xy"].step}
                    />
                  </EditPopover>
                  <EditPopover
                    label={"Scale"}
                    canReset={
                      !isEmpty(entranceAnimation?.animationConfig?.scale)
                    }
                    onReset={() => {
                      removeConfig("scale");
                    }}
                  >
                    <NumberInput
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.scale?.x ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("scale", "x", val);
                      }}
                      label={"X"}
                      // min={0}
                      // max={10}
                      // step={0.01}
                      min={INPUT_LIMITS["entrance-animations-scale-xy"].min}
                      max={INPUT_LIMITS["entrance-animations-scale-xy"].max}
                      step={INPUT_LIMITS["entrance-animations-scale-xy"].step}
                    />
                    <NumberInput
                      resetValue={""}
                      value={entranceAnimation?.animationConfig?.scale?.y ?? ""}
                      onChange={(val) => {
                        changeConfigDeep("scale", "y", val);
                      }}
                      label={"Y"}
                      // min={0}
                      // max={10}
                      // step={0.01}
                      min={INPUT_LIMITS["entrance-animations-scale-xy"].min}
                      max={INPUT_LIMITS["entrance-animations-scale-xy"].max}
                      step={INPUT_LIMITS["entrance-animations-scale-xy"].step}
                    />
                  </EditPopover>
                  <NumberInput
                    resetValue={undefined}
                    // min={0}
                    // max={1}
                    // step={0.01}
                    min={INPUT_LIMITS["entrance-animations-opacity"].min}
                    max={INPUT_LIMITS["entrance-animations-opacity"].max}
                    step={INPUT_LIMITS["entrance-animations-opacity"].step}
                    value={entranceAnimation?.animationConfig?.opacity}
                    onChange={(val) => {
                      changeConfig("opacity", val);
                    }}
                    label={"Opacity"}
                  />
                  <NumberInput
                    resetValue={undefined}
                    // min={0}
                    // max={10000}
                    // step={50}

                    min={INPUT_LIMITS["entrance-animations-duration"].min}
                    max={INPUT_LIMITS["entrance-animations-duration"].max}
                    step={INPUT_LIMITS["entrance-animations-duration"].step}
                    value={entranceAnimation?.animationConfig?.duration}
                    onChange={(val) => {
                      changeConfig("duration", val);
                    }}
                    label={"Duration"}
                  />
                  <NumberInput
                    resetValue={undefined}
                    // min={0}
                    // max={10000}
                    // step={100}
                    min={INPUT_LIMITS["entrance-animations-perspective"].min}
                    max={INPUT_LIMITS["entrance-animations-perspective"].max}
                    step={INPUT_LIMITS["entrance-animations-perspective"].step}
                    value={entranceAnimation?.animationConfig?.perspective}
                    onChange={(val) => {
                      changeConfig("perspective", val);
                    }}
                    label={"Perspective"}
                  />
                  <NumberInput
                    resetValue={undefined}
                    // min={0}
                    // max={10000}
                    // step={50}
                    min={INPUT_LIMITS["entrance-animations-delay"].min}
                    max={INPUT_LIMITS["entrance-animations-delay"].max}
                    step={INPUT_LIMITS["entrance-animations-delay"].step}
                    value={entranceAnimation?.animationConfig?.delay}
                    onChange={(val) => {
                      changeConfig("delay", val);
                    }}
                    label={"Delay"}
                  />
                  <ToggleInput
                    resetValue={undefined}
                    value={entranceAnimation?.animationConfig?.infinite}
                    onChange={(val) => {
                      changeConfig("infinite", val);
                    }}
                    label={"Infinite"}
                  />
                  {entranceAnimation?.animationConfig?.infinite && (
                    <>
                      <ToggleInput
                        resetValue={undefined}
                        value={entranceAnimation?.animationConfig?.yoyo}
                        onChange={(val) => {
                          changeConfig("yoyo", val);
                        }}
                        label={"Yoyo Style"}
                      />
                    </>
                  )}
                </div>
              </>
            )}
            <TextInput
              minLength={INPUT_LIMITS["entrance-animation-trigger"].minlength}
              maxLength={INPUT_LIMITS["entrance-animation-trigger"].maxlength}
              isResetVisible={false}
              value={entranceAnimation?.trigger?.element ?? ""}
              onChange={(val) => {
                changeEntranceDeepProp("trigger", "element", val);
              }}
              placeholder="self"
              label={"Trigger Element"}
            />
            <TextInput
              minLength={INPUT_LIMITS["entrance-animation-trigger"].minlength}
              maxLength={INPUT_LIMITS["entrance-animation-trigger"].maxlength}
              isResetVisible={false}
              value={entranceAnimation?.trigger?.start ?? ""}
              onChange={(val) => {
                changeEntranceDeepProp("trigger", "start", val);
              }}
              placeholder=""
              label={"Trigger Start"}
            />
            <TextInput
              minLength={INPUT_LIMITS["entrance-animation-trigger"].minlength}
              maxLength={INPUT_LIMITS["entrance-animation-trigger"].maxlength}
              isResetVisible={false}
              value={entranceAnimation?.trigger?.end ?? ""}
              onChange={(val) => {
                changeEntranceDeepProp("trigger", "end", val);
              }}
              placeholder=""
              label={"Trigger End"}
            />
            <ToggleInput
              resetValue={true}
              value={entranceAnimation?.initiallyHidden ?? true}
              onChange={(val) => {
                changeEntranceProp("initiallyHidden", val);
              }}
              label={"Hidden Initially"}
            />
            <ToggleInput
              resetValue={undefined}
              value={entranceAnimation?.direction === "to"}
              onChange={(val) => {
                changeEntranceProp("direction", val ? "to" : undefined);
              }}
              label={"Reverse"}
            />
            <SelectInput
              resetValue={""}
              value={entranceAnimation?.scrub}
              onChange={(val) => {
                changeEntranceProp("scrub", val);
              }}
              options={[
                { label: "-", value: "" },
                { label: "0.1", value: "0.1" },
                { label: "0.5", value: "0.5" },
                { label: "1 (Normal)", value: "1" },
                { label: "2", value: "2" },
                { label: "3", value: "3" },
                { label: "4", value: "4" },
              ]}
              label="Scroll Interpolation"
            />
            <SelectInput
              resetValue={undefined}
              value={entranceAnimation?.triggerAnchor}
              onChange={(val) => {
                changeEntranceProp("triggerAnchor", val);
              }}
              options={[
                { label: "-", value: "" },
                { label: "Top Left", value: "top left" },
                { label: "Top Center", value: "top center" },
                { label: "Top Right", value: "top right" },
                { label: "Center Left", value: "center left" },
                { label: "Center Center", value: "center center" },
                { label: "Center Right", value: "center right" },
                { label: "Bottom Left", value: "bottom left" },
                { label: "Bottom Center", value: "bottom center" },
                { label: "Bottom Right", value: "bottom right" },
              ]}
              label="Trigger Anchor"
            />
            <SelectInput
              resetValue={undefined}
              value={entranceAnimation?.transformOrigin}
              onChange={(val) => {
                changeEntranceProp("transformOrigin", val);
              }}
              options={[
                { label: "-", value: "" },
                { label: "Top Left", value: "top left" },
                { label: "Top Center", value: "top center" },
                { label: "Top Right", value: "top right" },
                { label: "Center Left", value: "center left" },
                { label: "Center Center", value: "center center" },
                { label: "Center Right", value: "center right" },
                { label: "Bottom Left", value: "bottom left" },
                { label: "Bottom Center", value: "bottom center" },
                { label: "Bottom Right", value: "bottom right" },
              ]}
              label="Transform Origin"
            />
          </div>
        </>

        <SelectInput
          isResetVisible={false}
          value={entranceAnimation?.easing}
          onChange={(val) => {
            setProp((props) => {
              props.entranceAnimation = {
                ...(entranceAnimation ? entranceAnimation : {}),
                easing: val,
              };
            });
          }}
          options={[
            { label: "Linear", value: "" },
            { label: "Power 1 In", value: "power1.in" },
            { label: "Power 1 Out", value: "power1.out" },
            { label: "Power 1 In Out", value: "power1.inOut" },
            { label: "Power 2 In", value: "power2.in" },
            { label: "Power 2 Out", value: "power2.out" },
            { label: "Power 2 In Out", value: "power2.inOut" },
            { label: "Power 3 In", value: "power3.in" },
            { label: "Power 3 Out", value: "power3.out" },
            { label: "Power 3 In Out", value: "power3.inOut" },
            { label: "Power 4 In", value: "power4.in" },
            { label: "Power 4 Out", value: "power4.out" },
            { label: "Power 4 In Out", value: "power4.inOut" },
            { label: "Back In", value: "back.in" },
            { label: "Back Out", value: "back.out" },
            { label: "Back In Out", value: "back.inOut" },
            { label: "Elastic In", value: "elastic.in" },
            { label: "Elastic Out", value: "elastic.out" },
            { label: "Elastic In Out", value: "elastic.inOut" },
            { label: "Bounce In", value: "bounce.in" },
            { label: "Bounce Out", value: "bounce.out" },
            { label: "Bounce In Out", value: "bounce.inOut" },
            {
              label: "Rough",
              value: `rough ({ template: none.out, strength: 1, points: 20, taper: none, randomize: true, clamp: false})`,
            },
            { label: "Slow", value: "slow (0.7, 0.7, false)" },
            { label: "Steps", value: "steps (12)" },
            { label: "Circ In", value: "circ.in" },
            { label: "Circ Out", value: "circ.out" },
            { label: "Circ In Out", value: "circ.inOut" },
            { label: "Expo In", value: "expo.in" },
            { label: "Expo Out", value: "expo.out" },
            { label: "Expo In Out", value: "expo.inOut" },
            { label: "Sine In", value: "sine.in" },
            { label: "Sine Out", value: "sine.out" },
            { label: "Sine In Out", value: "sine.inOut" },
          ]}
          label="Easing"
        />
      </Panel>
    </>
  );
}

function transformProps(props, supportProps) {
  if (props.entranceAnimation && !isEmpty(props.entranceAnimation)) {
    supportProps = {
      ...supportProps,
      className: {
        ...(supportProps.className ? supportProps.className : {}),
        [`entrance-${props.id}`]: true,
      },
    };
  }
  return supportProps;
}

function dynamicCSS(id, props) {
  const { entranceAnimation } = props;

  if (!(props.entranceAnimation && !isEmpty(props.entranceAnimation))) {
    return ``;
  }

  return ``;
}

const supportSchema = {
  id: "grigora/entrance-animations",
  name: "Entrance Animations",
  component: EntranceAnimations,
  transformProps: transformProps,
  dynamicCSS: dynamicCSS,
};

export default supportSchema;
